import { useTheme } from '@emotion/react';
import React, { memo, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ClubMemberViewModel, ReservationStoreSearchViewModel } from '~/lib/data-contract';
import { Radio, Text } from '~/shared/components';
import { useTranslation } from '~/shared/utils';
import { OpeningHoursTable } from '~/templates/pages/components/P180StorePage';
import {
    StyledOpeningHoursWrapper,
    StyledReservationRadioLabelContent,
    StyledReservationStore,
    StyledReservationStoreInfo,
    StyledReservationStores,
    StyledStockText,
    StyledStoreNameWrapper,
} from './styled';
import StarFillIcon from '$icons/star-fill.svg';

type ReserveStoresProps = {
    stores: ReservationStoreSearchViewModel[];
    isFetching: boolean;
    account?: ClubMemberViewModel;
};

export const ReserveStores = memo(({ stores, isFetching, account }: ReserveStoresProps) => {
    const { translate } = useTranslation();
    const { animations } = useTheme();
    const {
        register,
        getValues,
        formState: { submitCount },
    } = useFormContext();

    const storeIdInvalid = submitCount > 0 && !getValues('storeId');

    const selectedStoreId = getValues('storeId');

    const mappedStores = useMemo((): ReservationStoreSearchViewModel[] => {
        if (!account?.storeId) {
            return stores;
        } else {
            return stores.sort((a: ReservationStoreSearchViewModel): number => {
                if (a.id !== account.storeId) {
                    return 0;
                } else {
                    return -1;
                }
            });
        }
    }, [stores, account]);

    return (
        <StyledReservationStores>
            {mappedStores.map(
                ({ storeName, address, city, zipcode, stockStatus, id, openingHours }, index) => (
                    <StyledReservationStore
                        key={id}
                        initial={{
                            opacity: 0,
                            y: '10%',
                        }}
                        animate={{
                            opacity: 1,
                            y: '0',
                            transition: {
                                ...animations.springDefault,
                                delay: Math.min(index * 0.1, 1),
                            },
                        }}
                    >
                        <Radio
                            {...register('storeId')}
                            fillRow={true}
                            defaultValue={id}
                            disabled={isFetching}
                            isInvalid={storeIdInvalid}
                            alignment="start"
                            defaultChecked={id === account?.storeId}
                            label={
                                <StyledReservationRadioLabelContent>
                                    <StyledReservationStoreInfo>
                                        <StyledStoreNameWrapper>
                                            <Text variant="body" as="b">
                                                {storeName}
                                            </Text>
                                            {account?.storeId === id && <StarFillIcon />}
                                        </StyledStoreNameWrapper>
                                        <Text variant="bodySm" as="span">
                                            {address}, {zipcode} {city}
                                        </Text>
                                    </StyledReservationStoreInfo>
                                    <StyledStockText
                                        variant="bodySm"
                                        as="span"
                                        stockStatus={stockStatus}
                                    >
                                        {(() => {
                                            switch (stockStatus) {
                                                case 0:
                                                    return translate(
                                                        'reservation.stockStatus.notInStock'
                                                    );
                                                case 1:
                                                    return translate(
                                                        'reservation.stockStatus.fewInStock'
                                                    );
                                                case 2:
                                                    return translate(
                                                        'reservation.stockStatus.inStock'
                                                    );
                                            }
                                        })()}
                                    </StyledStockText>
                                    {id === selectedStoreId && (
                                        <StyledOpeningHoursWrapper>
                                            <OpeningHoursTable
                                                textVariantCaption={null}
                                                textVariantContent="bodySm"
                                                openingHours={openingHours}
                                                days="immediate-days"
                                            />
                                        </StyledOpeningHoursWrapper>
                                    )}
                                </StyledReservationRadioLabelContent>
                            }
                        />
                    </StyledReservationStore>
                )
            )}
        </StyledReservationStores>
    );
});
